import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Form } from 'antd';
import AuthService from 'services/authService';

import Section from 'layouts/Section/Section';

import AuthThankYouMessage from 'components/AuthJobseeker/AuthThankYouMessage/AuthThankYouMessage';

import useErrorCatch from 'hooks/useErrorCatch';
import { useTrackForm } from 'hooks/useTrackForm';

import parseCity from 'utils/parseCity';
import Services from 'utils/services';

import { COUNTDOWN } from 'constants/commons';
import { GTMEvents } from 'constants/enums';
import { AuthSteps, ProviderType } from 'constants/enums';

import AuthContent from './AuthContent/AuthContent';
import AuthTitle from './AuthTitle/AuthTitle';

const AuthJobseeker = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [newJobseeker, setNewJobseeker] = useState(false);
  const [authStep, setAuthStep] = useState<AuthSteps>(AuthSteps.CheckEmail);
  const [redirectLink, setRedirectLink] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(COUNTDOWN);

  const location = useLocation();
  const catchError = useErrorCatch();

  const joinUrl = import.meta.env.VITE_JOIN_URL;

  useEffect(() => {
    setAuthStep(AuthSteps.CheckEmail);
    setRedirectLink(null);
    setRedirectCountdown(COUNTDOWN);
    form.resetFields();
  }, [location.key]);

  const handleCheckEmail = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const { email } = form.getFieldsValue(['email']);
      const { exists, provider } = await AuthService.checkEmail({ email });

      if (exists) {
        setAuthStep(
          provider === ProviderType.Local
            ? AuthSteps.Login
            : AuthSteps.Registration
        );
      } else {
        setAuthStep(AuthSteps.Registration);
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: 'Operation Failed',
          description: 'Failed to check user e-mail registration'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendVerificationCode = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const { email, code } = form.getFieldsValue(['email', 'code']);
      const response = await AuthService.postVerificationCode({ email, code });

      if (response?.forwardLink) {
        setRedirectLink(response.forwardLink);
      } else {
        form.setFields([
          {
            name: 'code',
            errors: ['Invalid code. Please try again.']
          }
        ]);
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: 'Operation Failed',
          description: 'Failed to verify the code'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendRegistrationForm = async (locationRaw: string) => {
    // setIsLoading(true);
    const fieldsValues = form.getFieldsValue();

    const location = parseCity(locationRaw);
    const fields = {
      ...fieldsValues,
      location
    };

    try {
      await Services.postJobseeker(fields);
      setRedirectLink(joinUrl);
      setNewJobseeker(true);
      form.resetFields();
    } catch (error) {
      catchError({
        error,
        default: {
          message: "Can't submit form, please try again latter"
        }
      });
    } finally {
      // setIsLoading(false);
    }
  };

  useTrackForm({
    formId: 'inquiry',
    incompleteEvent: GTMEvents.AboutFormAbandonment,
    submittedEvent: GTMEvents.AboutFormSubmit
  });

  return (
    <Section background="--brand-primary-lighten">
      <AuthTitle authStep={authStep} />
      {redirectLink ? (
        <AuthThankYouMessage
          link={redirectLink}
          initialCountdown={redirectCountdown}
          newJobseeker={newJobseeker}
        />
      ) : (
        <Form
          name="auth"
          form={form}
          autoComplete="off"
          scrollToFirstError
          id="inquiry"
        >
          <AuthContent
            authStep={authStep}
            form={form}
            isLoading={isLoading}
            handleCheckEmail={handleCheckEmail}
            handleSendVerificationCode={handleSendVerificationCode}
            handleSendRegistrationForm={handleSendRegistrationForm}
            setAuthStep={setAuthStep}
          />
        </Form>
      )}
    </Section>
  );
};

export default AuthJobseeker;
